import { useRef } from 'react';
import {
  AutocompleteArrayInput,
  FilterForm,
  SearchInput,
  SelectInput,
  useListContext
} from 'react-admin';
import { Alert, Stack } from '@mui/material';

import type { SearchFacet } from '@src/types';
import { getCurrentPartner } from '@src/providers/dataProvider/shared';

// Facet tags that support selecting multiple values at once (OR'd together),
// instead of the single-value default.
const MULTI_SELECT_FACET_TAGS = new Set(['eligibility']);

// Component that renders filters with access to list context
const CatalogListToolbar = () => {
  const { meta } = useListContext();
  // Assertion here to handle React Admin's generic approach to dataProviders
  const latestSearchFacets = (meta?.filters as SearchFacet[] | undefined) || [];
  const facetsFailed = !!meta?.facetsFailed;

  // reware-api scopes the facet list to the current filter selection, so a
  // narrow (or zero-result) combination of filters can come back with fewer
  // facets than a broader one - or none at all. Filters must stay evergreen
  // regardless, so once a facet tag is seen for this partner we keep
  // rendering its input for the life of the page, refreshing its choices
  // whenever a newer response includes it again.
  // getCurrentPartner() (not requirePartner()) because this runs at render time -
  // a missing partner should surface as the data provider's error toast, not a
  // thrown error that crashes past react-admin's layout error boundary.
  const partnerCode = getCurrentPartner()?.code;
  const knownFacetsRef = useRef({ partnerCode, facets: new Map<string, SearchFacet>() });
  if (knownFacetsRef.current.partnerCode !== partnerCode) {
    knownFacetsRef.current = { partnerCode, facets: new Map() };
  }
  latestSearchFacets.forEach((facet) => {
    if (facet.tag) knownFacetsRef.current.facets.set(facet.tag, facet);
  });
  const searchFacets = Array.from(knownFacetsRef.current.facets.values());

  const inputs = [
    <SearchInput
      key="search"
      source="searchQuery"
      placeholder="Search by title, parent SKU, or SKU..."
      alwaysOn
      // SelectInput defaults to margin="dense"; SearchInput (built on
      // TextInput) doesn't default this at all (MUI falls back to "none"),
      // which otherwise makes it visibly taller than the other filters.
      margin="dense"
    />,
  ];

  // Every filter is driven entirely by the partner's catalog_management_search
  // shop config - each facet that comes back from searchFacets gets an input,
  // populated with whatever values that shop's search index returns for it.
  // rubricCode and eligibility are not special-cased: a shop that doesn't
  // configure them simply won't show them.
  searchFacets.forEach((facet: SearchFacet) => {
    const choices = facet.entries?.map(entry => ({
      id: entry.name,
      name: entry.name,
    })) || [];
    const label = facet.title || facet.tag;

    if (MULTI_SELECT_FACET_TAGS.has(facet.tag)) {
      inputs.push(
        <AutocompleteArrayInput
          key={facet.tag}
          source={facet.tag}
          choices={choices}
          label={label}
          alwaysOn
          // SelectInput defaults to margin="dense"; AutocompleteInput doesn't
          // default this at all (MUI falls back to "none"), which otherwise makes
          // this field visibly taller than the other filters next to it.
          margin="dense"
          sx={{ width: '220px' }}
        />
      );
      return;
    }

    inputs.push(
      <SelectInput
        key={facet.tag}
        source={facet.tag}
        choices={choices}
        label={label}
        alwaysOn
        resettable
        // rubricCode (Inspection Category) values tend to run longer than
        // other facets, so it gets extra width to avoid truncation.
        sx={{ width: facet.tag === 'rubricCode' ? '220px' : '150px' }}
      />
    );
  });

  return (
    <>
      {facetsFailed && (
        <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
          Some filter options couldn&apos;t be loaded - the filter bar may be incomplete.
        </Alert>
      )}
      <Stack direction="row" sx={{ mb: 2, pl: 2 }}>
        <FilterForm filters={inputs} />
      </Stack>
    </>
  );
};

export default CatalogListToolbar;
